/**
 * Kerntaak-score-kaart — Epic 4 (Beoordelen op kerntaken), Story 4.2/4.3
 *
 * Losgetrokken uit `StepKerntaken.tsx` (Story 4.2) zodat Story 4.3 hem
 * ongewijzigd qua vorm kan hergebruiken binnen de per-tijdvak-groepering
 * (S5) — dezelfde kaart, alleen de groepering eromheen is nieuw.
 */

import { useState, useRef } from 'react';
import { ScoreInput } from './ScoreInput';
import { useClickOutside } from '../../../hooks/useClickOutside';
import { roundTo1Decimal } from '@functieloonhuis/shared';

/** Ankerregel met vaardigheden — eerste 3 inline, >4 achter een "+N meer"-popover. */
function AnchorRow({ skills }: { skills: string[] }) {
  const [open, setOpen] = useState(false);
  const popoverRef = useRef<HTMLDivElement>(null);
  useClickOutside(popoverRef, () => setOpen(false), open);

  // UX-DR4 (letterlijk): "Bij >4 vaardigheden: eerste 3 + '+N meer'". Bij
  // precies 4 (of minder) tonen we ALLES inline, geen popover.
  const showPopover = skills.length > 4;
  const visible = showPopover ? skills.slice(0, 3) : skills;
  const rest = showPopover ? skills.slice(3) : [];

  return (
    <div className="flex items-center gap-1.5 flex-wrap text-[13px] text-gray-500">
      {/* Anchor-icoon als inline SVG, conform bestaand icoon-patroon in deze
          codebase (EvaluationWizard.tsx e.a.) — geen nieuw font-dependency
          (Material Symbols) toegevoegd, project laadt dat nergens. */}
      <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v13m0-13a3 3 0 100-6 3 3 0 000 6zm-8 5h2a6 6 0 006 6v0a6 6 0 006-6h2m-16 0a2 2 0 100-4 2 2 0 000 4zm16 0a2 2 0 100-4 2 2 0 000 4z" />
      </svg>
      <span>Ankers: {visible.join(' · ')}</span>
      {rest.length > 0 && (
        <div className="relative" ref={popoverRef}>
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            className="text-[#54949E] hover:underline font-medium"
          >
            +{rest.length} meer
          </button>
          {open && (
            <div className="absolute z-10 mt-1 left-0 w-56 bg-white border border-[#E2E8F0] rounded-lg shadow-lg p-3 text-[13px] text-gray-600">
              {rest.join(' · ')}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export interface KerntaakScoreCardProps {
  coreTaskId: number;
  coreTaskName: string;
  timePercentage: number;
  skills: string[];
  score: number | null;
  toelichting: string;
  /** Punten die deze kerntaak nu bijdraagt binnen ZIJN tijdvak-blok (max 50 x pct/100), volle precisie. */
  earnedPointsWithinPeriod: number;
  onScoreChange: (score: number) => void;
  onToelichtingChange: (toelichting: string) => void;
}

/** Eén kerntaak-kaart: kop met naam + gewicht-chip, ankerregel, score, toelichting, live punteneffect. */
export function KerntaakScoreCard({
  coreTaskId,
  coreTaskName,
  timePercentage,
  skills,
  score,
  toelichting,
  earnedPointsWithinPeriod,
  onScoreChange,
  onToelichtingChange,
}: KerntaakScoreCardProps) {
  const maxPointsForTask = 50 * (timePercentage / 100);

  return (
    <div
      data-testid={`kerntaak-card-${coreTaskId}`}
      className="bg-white rounded-lg border border-[#E2E8F0] shadow-sm overflow-hidden"
    >
      <div className="px-6 py-4 bg-gray-50 border-b border-[#E2E8F0]">
        <div className="flex items-start justify-between">
          <h3 className="text-lg font-medium text-[#1E293B]">{coreTaskName}</h3>
          <span className="px-3 py-1 border border-[#54949E] text-[#54949E] text-sm font-medium rounded-full whitespace-nowrap">
            {timePercentage}% van je tijd
          </span>
        </div>
        {skills.length > 0 && (
          <div className="mt-1">
            <AnchorRow skills={skills} />
          </div>
        )}
      </div>

      <div className="px-6 py-4 space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Jouw beoordeling</label>
          <ScoreInput value={score} onChange={onScoreChange} showLabels taskId={coreTaskId} />
          <p className="mt-2 text-xs text-gray-400">1 = ver onder verwachting &middot; 5 = ver boven verwachting</p>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Toelichting (optioneel)</label>
          <textarea
            rows={2}
            value={toelichting}
            onChange={(e) => onToelichtingChange(e.target.value)}
            placeholder="Geef een toelichting op je beoordeling..."
            className="w-full px-3 py-2 border border-[#E2E8F0] rounded-lg focus:ring-2 focus:ring-[#54949E] focus:border-[#54949E]"
          />
        </div>

        <p className="text-right text-xs text-gray-500">
          Telt voor {roundTo1Decimal(maxPointsForTask).toFixed(1)} van de 50 punten &middot; huidige score:{' '}
          {roundTo1Decimal(earnedPointsWithinPeriod).toFixed(1)}
        </p>
      </div>
    </div>
  );
}
